import React, { useEffect, useState } from 'react';
import { ModalWrapper } from '@/shared';

interface FinancialRecord {
  id: string;
  title: string;
  amount: number;
  status: 'paid' | 'partial' | 'overdue';
}

interface DeleteFinancialRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (id: string) => void;
  record: FinancialRecord;
}

export const DeleteFinancialRecordModal: React.FC<
  DeleteFinancialRecordModalProps
> = ({ isOpen, onClose, onConfirm, record }) => {
  const [isChecked, setIsChecked] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleConfirmDelete = () => {
    if (!isChecked) {
      setShowError(true);
      return;
    }
    onConfirm(record.id);
    onClose();
  };

  const handleCancelDelete = () => {
    if (isChecked) {
      setIsChecked(false);
      setShowError(false);
    }
    onClose();
  };

  // Reset local state when modal opens or record changes
  useEffect(() => {
    if (isOpen) {
      setIsChecked(false);
      setShowError(false);
    }
  }, [isOpen, record.id]);
  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-center w-full min-h-full">
        <div className="w-xl p-8 bg-white rounded-4xl">
          <svg
            className="mb-8"
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M21.3482 1.77785C21.3483 1.30704 21.5353 0.855532 21.8681 0.522537C22.2009 0.189543 22.6523 0.00230787 23.1232 0.00197656L40.872 0.000976562C41.343 0.00206831 41.7942 0.189822 42.127 0.523084C42.4597 0.856346 42.6467 1.30793 42.647 1.77885V6.16848H21.3482V1.77785ZM50.5308 61.4582C50.4855 62.1504 50.1774 62.7991 49.6695 63.2715C49.1616 63.7439 48.4924 64.0043 47.7988 63.9995H15.9924C15.299 63.9976 14.6319 63.7335 14.1252 63.2601C13.6185 62.7867 13.3096 62.1392 13.2607 61.4475L10.5389 21.6327H53.4357L50.5314 61.4577L50.5308 61.4582ZM57.6018 18.0267H6.39453V13.9017C6.39529 12.8077 6.83017 11.7587 7.60369 10.985C8.3772 10.2113 9.42613 9.77622 10.5202 9.77523L53.4757 9.77385C54.5696 9.77554 55.6182 10.211 56.3915 10.9848C57.1648 11.7585 57.5996 12.8074 57.6007 13.9014V18.0264L57.6018 18.0267ZM23.753 54.5081C23.753 54.7448 23.7997 54.9793 23.8903 55.198C23.9809 55.4167 24.1136 55.6154 24.281 55.7828C24.4484 55.9502 24.6472 56.083 24.8659 56.1736C25.0846 56.2642 25.319 56.3108 25.5558 56.3108C25.7925 56.3108 26.0269 56.2642 26.2457 56.1736C26.4644 56.083 26.6631 55.9502 26.8305 55.7828C26.9979 55.6154 27.1307 55.4167 27.2213 55.198C27.3119 54.9793 27.3585 54.7448 27.3585 54.5081V29.2272C27.3547 28.7518 27.1633 28.2971 26.8258 27.9622C26.4883 27.6273 26.0322 27.4393 25.5567 27.4391C25.0813 27.4389 24.625 27.6266 24.2873 27.9613C23.9496 28.296 23.7578 28.7505 23.7537 29.226V54.5081H23.753ZM36.6155 54.5081C36.6155 54.9863 36.8055 55.4449 37.1436 55.783C37.4817 56.1211 37.9403 56.3111 38.4185 56.3111C38.8967 56.3111 39.3553 56.1211 39.6934 55.783C40.0316 55.4449 40.2215 54.9863 40.2215 54.5081V29.2272C40.2177 28.7517 40.0262 28.297 39.6887 27.962C39.3512 27.6271 38.895 27.439 38.4195 27.4389C37.9439 27.4387 37.4876 27.6264 37.1499 27.9611C36.8121 28.2959 36.6203 28.7505 36.6162 29.226L36.6155 54.5081Z"
              fill="#E03A3D"
            />
          </svg>

          <div className="mb-8">
            <h4 className="text-3xl font-bold text-text-primary mb-2 ">
              Delete record ({record.title})
            </h4>
          </div>

          <div className="flex items-center gap-4 mb-8">
            <input
              type="checkbox"
              id="confirmDelete"
              checked={isChecked}
              onChange={(e) => {
                setIsChecked(e.target.checked);
                if (e.target.checked) setShowError(false);
              }}
              className="mt-1 cursor-pointer"
            />
            <label
              htmlFor="confirmDelete"
              className="text-text-secondary cursor-pointer"
            >
              I understand that all members and records linked to this house
              will be deleted permanently.*
            </label>
          </div>

          {/* Error message */}
          {showError && (
            <p className="text-sm text-red-500 mb-4">
              Please confirm before deleting this record.
            </p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <button
              className="p-2  text-[#5761EB] text-lg font-bold  cursor-pointer rounded-md hover:bg-[#5761EB] hover:text-white transition-colors"
              onClick={handleCancelDelete}
            >
              Cancle
            </button>
            <button
              // className="p-2  text-[#891518] text-lg font-bold cursor-pointer"
              className={`p-2 text-lg font-bold rounded-md border transition-colors ${
                isChecked
                  ? 'border-[#891518] text-[#891518] hover:bg-[#891518] hover:text-white'
                  : 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-100'
              }`}
              onClick={handleConfirmDelete}
            >
              Delete house
            </button>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};
